import {
  inject,
  Injectable,
  signal,
  WritableSignal,
} from '@angular/core';
import { AvailabilitySpaBooking } from '../../../../../core/model/availability-spa-booking.model';
import { Credit } from '../../../../../core/model/credit.model';
import { Invoice } from '../../../../../core/model/invoice.model';
import { RendezVous } from '../../../../../core/model/rendez-vous.model';
import { Seance } from '../../../../../core/model/seance.model';
import { AvailableSpaBooking } from '../../../../../core/model/spa-booking.model';
import { FirebaseFunctions } from '../../app.config';

@Injectable({
  providedIn: 'root',
})
/**
 * Cart Service
 */
export class AccountService {
  dataChanged = signal(new Date());
  credits: WritableSignal<{ timeCredit: Credit; session: Credit } | null> = signal(null);
  private functions = inject(FirebaseFunctions);
  #invoicesCache: Invoice[] | null = null;

  async refreshCredits(): Promise<void> {
    const credits = await this.functions.httpsCallable<null, {
      timeCredit: Credit,
      session: Credit
    }>('aquao-credits', null);
    this.credits.set(credits);
  }

  async seances(centerErpId: number, from: string, to: string): Promise<Seance[]> {
    return this.functions.httpsCallable<{
        centerErpId: number,
        from: string,
        to: string
      }, Seance[]>('aquao-seances', { centerErpId, from, to })
      .then(res => res.map(s => new Seance(s)));
  }

  async rendezVous(from: string, to: string): Promise<RendezVous[]> {
    return this.functions.httpsCallable<{
        from: string,
        to: string
      }, RendezVous[]>('aquao-rendezVous', { from, to })
      .then(res => res.map(s => new RendezVous(s)))
      .then(res => {
        res.sort((a, b) => {
          if (a.date === b.date) {
            return b.start.localeCompare(a.start);
          }
          return b.date.localeCompare(a.date);
        })
        return res;
      });
  }

  async invoices(from = 0): Promise<Invoice[]> {
    return this.functions.httpsCallable<{ from: number }, Invoice[]>('aquao-invoices', { from })
      .then(invoices => invoices.map(invoice => new Invoice(invoice)))
  }

  participateSeance(seance: Seance): Promise<void> {
    return this.functions.httpsCallable<{
      erpId: number,
    }, void>('aquao-seanceParticipate', { erpId: seance.erpId });
  }

  waitSeance(seance: Seance, toggleHour: number): Promise<void> {
    return this.functions.httpsCallable<{
      erpId: number, toggleHour: number
    }, void>('aquao-seanceWait', { erpId: seance.erpId, toggleHour });
  }

  cancelWaitSeance(erpId: number): Promise<void> {
    return this.functions.httpsCallable<{
      erpId: number,
    }, void>('aquao-seanceCancelWait', { erpId });
  }

  cancelSeance(erpId: number): Promise<void> {
    return this.functions.httpsCallable<{
      erpId: number,
    }, void>('aquao-seanceCancel', { erpId });
  }

  availableSpaBooking(centerErpId: number): Promise<AvailableSpaBooking[]> {
    return this.functions.httpsCallable<{
      centerErpId: number,
    }, AvailableSpaBooking[]>('aquao-availableAllSpa', { centerErpId });

  }

  availableSpaForProductGift(centerErpId: number, produitCadeauOid: number): Promise<AvailableSpaBooking[]> {
    return this.functions.httpsCallable<{
      centerErpId: number,
      produitCadeauOid: number
    }, AvailableSpaBooking[]>('aquao-availableSpaForProductGift', { centerErpId, produitCadeauOid });

  }

  availabilitySpaBooking(centerErpId: number, erpId: number, from: string, to: string, duo = false
  ): Promise<AvailabilitySpaBooking[]> {
    return this.functions.httpsCallable<{
      centerErpId: number,
      erpId: number,
      from: string,
      to: string,
      duo?: boolean
    }, AvailabilitySpaBooking[]>('aquao-availabilitySpa', { centerErpId, erpId, from, to, ...(duo ? { duo } : {}) });
  }

  /**
   * Réserve ou simule un créneau spa auprès d'Aquao.
   * @param date Date du rendez-vous au format `YYYY-MM-DD`
   * @param time Heure de début du rendez-vous
   * @param userErpId Identifiant Aquao du client
   * @param productErpId Identifiant Aquao de la prestation spa
   * @param fromErpId Identifiant Aquao du droit consommé (bon cadeau, abonnement ou option)
   * @param type Nature du droit consommé
   * @param centerErpId Identifiant Aquao de l'établissement
   * @param simulation true pour vérifier la disponibilité sans créer le rendez-vous
   * @param commentaires Commentaires client facultatif, ignoré en simulation
   * @param duo true pour une cabine duo (uniquement pour une option, avant paiement)
   * @returns Heure de début réellement retenue par Aquao
   */
  async planSpa(date: string, time: string, userErpId: number, productErpId: number, fromErpId: number,
    type: 'voucher' | 'subscription' | 'option', centerErpId: number, simulation: boolean,
    commentaires?: string, duo = false
  ): Promise<string> {
    const trimmedCommentaires = commentaires?.trim();
    const { start } = await this.functions.httpsCallable<{
      date: string,
      time: string,
      userErpId: number,
      productErpId: number,
      fromErpId: number,
      type: 'voucher' | 'subscription' | 'option',
      centerErpId: number,
      simulation: boolean,
      commentaires?: string,
      duo?: boolean
    }, { start: string }>('aquao-planSpa', {
      date,
      time,
      userErpId,
      productErpId,
      fromErpId,
      type,
      centerErpId,
      simulation,
      ...(!simulation && trimmedCommentaires ? { commentaires: trimmedCommentaires } : {}),
      ...(duo ? { duo } : {})
    });
    return start;
  }
}
